import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import { getToken, getEmail, clearSession } from "@/lib/auth";
import { toast } from "sonner";
import { LogOut, Eye, Check, X, Inbox, BookOpenCheck, Wallet, Clock, CalendarDays, Receipt, Download, FileText, BadgeCheck, Search, MessageSquare, Trash2, Edit2, CalendarSearch, CalendarRange, Activity, BedDouble, FileEdit, UserCog, Megaphone, ShieldCheck, Calculator, Home, ChevronRight, Users, LayoutGrid, Menu, ListChecks, TrendingUp, Car, Star, LogIn, LogOut as LogOutIcon, Bell, Sparkles, Building2, Plus, ReceiptText, Image as ImageIcon, FileSpreadsheet, AlertTriangle, Copy } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { formatDate, formatDateTime } from "@/lib/dates";
import CreateGiftTicketDialog from "@/components/CreateGiftTicketDialog";
import GuideCalendarTab from "@/components/admin/GuideCalendarTab";
import ApiLogsTab from "@/components/admin/ApiLogsTab";
import RoomManagementTab from "@/components/admin/RoomManagementTab";
import SiteContentTab from "@/components/admin/SiteContentTab";
import PromoSettingsTab from "@/components/admin/PromoSettingsTab";
import RatePlansTab from "@/components/admin/RatePlansTab";
import RmsTab from "@/components/admin/RmsTab";
import FinanceImportTab from "@/components/admin/FinanceImportTab";
import PaymentMethodsTab from "@/components/admin/PaymentMethodsTab";
import ComplianceTab from "@/components/admin/ComplianceTab";
import ManagementTab from "@/components/admin/ManagementTab";
import ProfileTab from "@/components/admin/ProfileTab";
import EmployeesTab from "@/components/admin/EmployeesTab";
import ExportTab from "@/components/admin/ExportTab";
import ShuttleApprovalsTab from "@/components/admin/ShuttleApprovalsTab";
import CustomersTab from "@/components/admin/CustomersTab";
import ReviewsTab from "@/components/admin/ReviewsTab";
import OtaSyncDialog from "@/components/admin/OtaSyncDialog";
import TrashDialog from "@/components/admin/TrashDialog";
import { NewBookingDialog } from "@/components/admin/CalendarTab";
import CalendarTab from "@/components/admin/CalendarTab";
import CheckinsTab from "@/components/admin/CheckinsTab";
import SyncAlertsBanner from "@/components/admin/SyncAlertsBanner";
import GroupHomeTab from "@/components/admin/GroupHomeTab";
import DeadlinesTab from "@/components/admin/DeadlinesTab";
import ContractsTab from "@/components/admin/ContractsTab";
import GuideIntegrationTab from "@/components/admin/GuideIntegrationTab";
import ReceiptsQuotesTab from "@/components/admin/ReceiptsQuotesTab";
import RegulationsTab from "@/components/admin/RegulationsTab";
import PhotoLibraryTab from "@/components/admin/PhotoLibraryTab";
import HomeDashboard from "@/components/admin/HomeDashboard";
import PendingBookingsBanner from "@/components/admin/PendingBookingsBanner";
import IcalInboxTab from "@/components/admin/IcalInboxTab";
import BookingHistoryTab from "@/components/admin/BookingHistoryTab";
import CleaningPlanTab from "@/components/admin/CleaningPlanTab";
import { propertyTone } from "@/lib/propertyTone";
import { ADMIN_NAV_GROUPS, TOP_MENU_LABELS, todayISO } from "@/components/admin/dashboardConfig";
import { labelFor, colorFor, BookingStatusLegend } from "@/lib/bookingStatus";
import { buildWhatsAppLink, waMessageForFlexibleRequest, extractPhone } from "@/lib/whatsapp";
import { MessageCircle } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { ExternalLink, CalendarClock, ChevronDown, Book, Scale, Pencil } from "lucide-react";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";

// Macro-categorie del pannello admin (sidebar) sono definite in dashboardConfig.
// ADMIN_NAV_GROUPS, HOME_TILES, TOP_MENU_LABELS, helpers di tile-usage sono
// importati sopra.

// Macro-categorie del pannello admin (sidebar) sono ora importate da
// `@/components/admin/dashboardConfig` come `ADMIN_NAV_GROUPS`.

export default function AdminDashboardPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messagesRelevantOnly, setMessagesRelevantOnly] = useState(false);
  const [stats, setStats] = useState(null);
  const [shuttlePending, setShuttlePending] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [activeTab, _setActiveTab] = useState("home");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Allow deep-linking to a specific tab via ?tab=messages (used by GroupHomeTab badges)
  // Also supports ?tab=bookings&code=XXXX to jump to a specific reservation.
  // iter173: re-run on every location.search change so that same-page
  // navigations (es. dal calendario → Ricevute con `?manual=1`) attivano
  // il tab corretto.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get("tab");
      const code = params.get("code");
      if (t) {
        _setActiveTab(t);
      }
      if (code) {
        // Pre-fill the bookings search so the row is visible immediately
        // and the host can tap it to open the detail modal.
        _setActiveTab("bookings");
        setSearch(code);
      }
      if (t || code) {
        // Clean URL without reloading (but PRESERVE `manual` / `q` — those
        // are consumed by the target tab component, not by us).
        const url = new URL(window.location.href);
        url.searchParams.delete("tab");
        url.searchParams.delete("code");
        window.history.replaceState({}, "", url.toString());
      }
    } catch (e) { console.warn("[AdminDashboard] deep-link parse failed", e); }
  }, [location.search]);

  // Last visited tab (other than home/bookings) — persisted, shown in top bar
  const [lastVisited, setLastVisited] = useState(() => localStorage.getItem("admin_last_visited") || "");

  // Notification counts (sidebar dots + Contabilità Scadenze badge)
  const [notifCounts, setNotifCounts] = useState({});
  // Per-section "seen" map → drives dot visibility
  const [seenMap, setSeenMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem("admin_seen_map") || "{}"); }
    catch { return {}; }
  });

  // Wrapper: setActiveTab also tracks last visited and marks section as seen
  const setActiveTab = (tab) => {
    _setActiveTab(tab);
    // Track last visited if not one of the always-pinned ones
    if (tab && !["home", "bookings", "group-home"].includes(tab)) {
      localStorage.setItem("admin_last_visited", tab);
      setLastVisited(tab);
    }
    // Mark this section as seen (for dot dismissal)
    const newSeen = { ...seenMap, [tab]: Date.now() };
    setSeenMap(newSeen);
    localStorage.setItem("admin_seen_map", JSON.stringify(newSeen));
  };

  // Periodically refresh notification counts
  useEffect(() => {
    let mounted = true;
    const fetchCounts = async () => {
      try {
        const r = await api.get("/admin/notifications/counts");
        if (mounted) setNotifCounts(r.data || {});
      } catch (e) { if (mounted) console.warn("notif counts fetch failed:", e); }
    };
    fetchCounts();
    const intv = setInterval(fetchCounts, 60000);
    return () => { mounted = false; clearInterval(intv); };
  }, []);

  // Auto-mark all contact messages as read when the host opens the Messaggi tab.
  // This drops the badge counter once she has actually seen the inbox — same
  // pattern as Gmail / WhatsApp.
  useEffect(() => {
    if (activeTab !== "messages") return;
    if (!(notifCounts.messages > 0)) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        await api.post("/admin/messages/mark-all-read");
        if (cancelled) return;
        // Optimistically clear the badge immediately
        setNotifCounts((prev) => ({ ...prev, messages: 0 }));
      } catch (e) {
        console.warn("[messages] auto mark-read failed:", e);
      }
    }, 1200); // give the host a moment to actually see the list
    return () => { cancelled = true; clearTimeout(t); };
  }, [activeTab, notifCounts.messages]);

  // Helper: does a sidebar tab have a "new" dot?
  const sectionHasDot = (tabId, count) => {
    if (!count || count <= 0) return false;
    const seenAt = seenMap[tabId] || 0;
    // If never seen, dot is on. If seen within last 30s, hide. Otherwise on (refreshing counts).
    return Date.now() - seenAt > 30000 || activeTab !== tabId;
  };

  // Sidebar macro-groups: collapsible.
  // Sempre tutte chiuse all'apertura del sito (no persistenza). L'auto-expand
  // sotto apre solo il gruppo che contiene la tab attiva, se serve.
  const [openGroups, setOpenGroups] = useState({
    home: false, availability: false, bookings: false,
    communication: false, system: false, compliance: false,
  });
  const toggleGroup = (gid) => {
    setOpenGroups((p) => ({ ...p, [gid]: !p[gid] }));
  };

  // Auto-expand the group that contains the active tab (in-memory only)
  useEffect(() => {
    const ownerGroup = ADMIN_NAV_GROUPS.find((g) => g.items.some((it) => it.id === activeTab));
    if (ownerGroup && !openGroups[ownerGroup.id]) {
      setOpenGroups((p) => ({ ...p, [ownerGroup.id]: true }));
    }
  }, [activeTab]);

  // Cleanup obsolete persisted state from previous versions
  useEffect(() => {
    try { localStorage.removeItem("admin_open_groups"); }
    catch (e) { console.warn("[AdminDashboard] localStorage clear failed", e); }
  }, []);

  // Forces NotifyCard dots to recompute after a click → setItem fires storage in same tab
  const [notifTick, setNotifTick] = useState(0);
  const bumpNotifs = () => setNotifTick((x) => x + 1);

  // OTA sync dialog state (Verifica e sincronizza)
  const [otaSyncOpen, setOtaSyncOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [trashCount, setTrashCount] = useState(0);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editRooms, setEditRooms] = useState([]);

  // Filters & sorting
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterReceipt, setFilterReceipt] = useState("all");
  const [filterDate, setFilterDate] = useState(""); // YYYY-MM-DD
  // Date filter mode:
  //   "stay"     → booking covers filterDate (check_in <= D < check_out) — default
  //   "checkin"  → check_in === filterDate (used by "Check-in oggi" chip)
  //   "checkout" → check_out === filterDate (used by "Check-out oggi" chip)
  const [filterDateMode, setFilterDateMode] = useState("stay");
  const [search, setSearch] = useState("");
  // Iter163: default sort was "checkin_desc" (furthest future first). The
  // host asked for "checkin_asc" ("prima il più vicino") as the default so
  // upcoming stays surface at the top. Persisted per-session in the URL
  // if the user picks a different value (see setSortBy usage).
  const [sortBy, setSortBy] = useState("checkin_asc");
  // Iter159: default view hides prenotazioni already ended (check_out < today).
  // The host almost always cares about today + future stays; showing hundreds
  // of past "Completata" rows pushes the actionable ones off-screen and makes
  // the built-in search feel useless. Toggle stays sticky in localStorage so
  // the host can flip it OFF once and keep the wider view across sessions.
  const [hidePast, setHidePast] = useState(() => {
    try {
      const v = localStorage.getItem("admin_bookings_hide_past");
      return v === null ? true : v === "1";
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem("admin_bookings_hide_past", hidePast ? "1" : "0"); } catch (_) { /* localStorage unavailable */ }
  }, [hidePast]);

  const filteredBookings = useMemo(() => {
    const today = todayISO();
    let list = [...bookings];
    // Iter159: hide already-ended stays by default. A stay is "past" iff
    // check_out < today (i.e., yesterday and earlier). We still show today
    // even if the guest already left this morning — the host may still need
    // to open it (deposit refund, review reply, receipt upload). Bookings
    // without check_out (rare) are kept.
    // Search bypass: if the host is actively searching (name/code/email),
    // we want to surface ALL matches — past reservations included — so a
    // lookup like "Rossi 2024" still works even with the default view.
    if (hidePast && !search.trim()) {
      list = list.filter((b) => !b.check_out || b.check_out >= today);
    }
    if (filterStatus === "needs_assignment") {
      // Special pseudo-filter: walk-in bookings still missing a real room/price
      list = list.filter((b) => b.needs_room_assignment === true || b.room_id === "tbd_walkin");
    } else if (filterStatus === "checked_in") {
      // "Check-in fatto" — allineato ESATTAMENTE all'elenco base del tab
      // Check-in (CheckinsTab.jsx righe 99-104): tutti gli ospiti che hanno
      // completato il check-in — status "checked_in", "completed", oppure
      // ingressi walk-in tramite App Guida. Prima filtrava solo
      // status="checked_in" ma nel flusso normale la maggior parte passa
      // direttamente da "confirmed" a "completed" saltando "checked_in"
      // → chip a 0 (segnalazione host 03/07).
      list = list.filter(
        (b) => b.status === "checked_in" || b.status === "completed" || b.source === "walk_in"
      );
    } else if (filterStatus !== "all") list = list.filter((b) => b.status === filterStatus);
    if (filterReceipt === "with") list = list.filter((b) => b.receipt);
    if (filterReceipt === "without") list = list.filter((b) => !b.receipt);
    if (filterReceipt === "to_verify") list = list.filter((b) => b.receipt && b.status === "confirmed");
    if (filterDate) {
      if (filterDateMode === "checkin") {
        list = list.filter((b) => b.check_in === filterDate);
      } else if (filterDateMode === "checkout") {
        list = list.filter((b) => b.check_out === filterDate);
      } else {
        // stay range (default): booking covers filterDate iff check_in <= D < check_out
        list = list.filter((b) => b.check_in <= filterDate && filterDate < b.check_out);
      }
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(
        (b) =>
          (b.code || "").toLowerCase().includes(s) ||
          (b.group_code || "").toLowerCase().includes(s) ||
          (b.external_id || "").toString().toLowerCase().includes(s) ||
          `${b.guest?.first_name || ""} ${b.guest?.last_name || ""}`.toLowerCase().includes(s) ||
          (b.guest?.email || "").toLowerCase().includes(s) ||
          (b.guest?.phone || "").toLowerCase().includes(s),
      );
    }
    const cmpStr = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
    if (sortBy === "created_desc") list.sort((a, b) => cmpStr(b.created_at, a.created_at));
    else if (sortBy === "created_asc") list.sort((a, b) => cmpStr(a.created_at, b.created_at));
    else if (sortBy === "checkin_asc") list.sort((a, b) => cmpStr(a.check_in, b.check_in));
    else if (sortBy === "checkin_desc") list.sort((a, b) => cmpStr(b.check_in, a.check_in));
    else if (sortBy === "total_desc") list.sort((a, b) => (Number(b.total) || 0) - (Number(a.total) || 0));
    return list;
  }, [bookings, hidePast, filterStatus, filterReceipt, filterDate, filterDateMode, search, sortBy]);

  // ----- DASHBOARD NOTIFICATION METRICS -----
  // Recompute the counts and the "has-new" flag (based on localStorage last_viewed)
  // every time bookings/shuttle/reviews change OR notifTick increments (after a click).
  const notifications = useMemo(() => {
    const today = todayISO();
    const ACTIVE = ["confirmed", "completed", "checked_in"];
    const checkinsToday = bookings.filter((b) => b.check_in === today && ACTIVE.includes(b.status));
    const checkoutsToday = bookings.filter((b) => b.check_out === today && ACTIVE.includes(b.status));
    const receiptsToVerify = bookings.filter((b) => b.receipt && b.status === "confirmed");
    const extras = shuttlePending + receiptsToVerify.length;

    const lastViewed = (key) => Number(localStorage.getItem(key) || 0);
    const newestBookingTs = (list) => list.reduce((m, b) => Math.max(m, Date.parse(b.created_at || 0) || 0), 0);
    const newestReviewTs = reviews.reduce((m, r) => Math.max(m, Date.parse(r.created_at || 0) || 0), 0);

    return {
      checkin: {
        count: checkinsToday.length,
        hasNew: checkinsToday.length > 0 && newestBookingTs(checkinsToday) > lastViewed("nv_checkin"),
      },
      checkout: {
        count: checkoutsToday.length,
        hasNew: checkoutsToday.length > 0 && newestBookingTs(checkoutsToday) > lastViewed("nv_checkout"),
      },
      extras: {
        count: extras,
        hasNew: extras > 0,  // any pending extra is "fresh" until consumed
      },
      reviews: {
        count: reviews.length,
        hasNew: reviews.length > 0 && newestReviewTs > lastViewed("nv_reviews"),
      },
    };
  }, [bookings, shuttlePending, reviews, notifTick]);

  const markViewedAndGo = (key, tab, extraSetters) => {
    localStorage.setItem(key, String(Date.now()));
    if (extraSetters) extraSetters();
    setActiveTab(tab);
    bumpNotifs();
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      // Use allSettled so a failing secondary endpoint (e.g. stats / messages)
      // does NOT prevent the primary bookings list from loading.
      const [b, m, s, sp, rv] = await Promise.allSettled([
        api.get("/admin/bookings"),
        api.get("/admin/messages"),
        api.get("/admin/stats"),
        api.get("/admin/shuttle-pending"),
        api.get("/admin/reviews"),
      ]);
      // If ANY call returned 401 → session expired, redirect to login
      const anyAuthFail = [b, m, s, sp, rv].some(
        (r) => r.status === "rejected" && r.reason?.response?.status === 401,
      );
      if (anyAuthFail) {
        clearSession();
        // iter238 — preserva il deep-link (tab, code, ecc.) così dopo il
        // login l'host arriva direttamente all'elenco prenotazioni
        // richiesto dal tasto Telegram/email, invece che a pagina bianca.
        const next = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
        navigate(`/admin/login?next=${next}`);
        return;
      }
      // Each one degrades to a safe default if it failed
      if (b.status === "fulfilled") setBookings(b.value.data);
      else { console.error("[Dashboard] /admin/bookings failed", b.reason); setBookings([]); }
      if (m.status === "fulfilled") setMessages(m.value.data);
      else setMessages([]);
      if (s.status === "fulfilled") setStats(s.value.data);
      if (sp.status === "fulfilled") {
        setShuttlePending(
          (sp.value.data?.counts?.pending_bookings || 0) + (sp.value.data?.counts?.pending_standalone || 0),
        );
      } else setShuttlePending(0);
      if (rv.status === "fulfilled") setReviews(rv.value.data?.reviews || []);
      else setReviews([]);

      // Surface non-auth errors so the admin knows something is degraded
      const otherFailures = [b, m, s, sp, rv].filter(
        (r) => r.status === "rejected" && r.reason?.response?.status !== 401,
      );
      if (otherFailures.length > 0) {
        toast.error(`Alcuni dati non si sono caricati (${otherFailures.length}/5 endpoint in errore)`);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) {
      // Preserve current URL (tab + query) so post-login can deep-link back
      const next = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
      navigate(`/admin/login?next=${next}`);
      return;
    }
    refresh();
  }, [navigate, refresh]);

  const updateStatus = async (id, status) => {
    try {
      const r = await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`✓ ${status}`);
      // If the confirmation just went through, offer a one-tap WhatsApp
      // link the host can send to the guest — pre-filled with the code
      // and the resume URL. The user asked for this so a confirmation
      // reaches the fastest channel (WhatsApp) without retyping.
      if (r?.data?.whatsapp_url && status === "confirmed") {
        toast.success(
          <span>
            Prenotazione confermata: <strong>{r.data.booking_code}</strong>. Ora invia l'avviso su WhatsApp →
            <a
              href={r.data.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 underline text-emerald-600 font-semibold"
              data-testid="wa-confirm-link"
            >
              💬 Invia conferma
            </a>
          </span>,
          { duration: 15000 },
        );
      }
      refresh();
    } catch {
      toast.error(t.common.error);
    }
  };

  const deleteBooking = async (booking) => {
    const code = booking.code || booking.id;
    const guestName = [booking.guest?.first_name, booking.guest?.last_name].filter(Boolean).join(" ") || "(senza nome)";
    if (!window.confirm(
      `Eliminare la prenotazione ${code} di ${guestName}?\n\n` +
      `Verrà spostata nel CESTINO (resta recuperabile per 7 giorni).\n` +
      `Sparirà subito da TUTTI i menu correlati:\n` +
      `• Prenotazioni · Check-in · Calendario · Channel Manager\n` +
      `• Finanze · Ricevute · Richieste navetta · Sync OTA\n\n` +
      `Potrai ripristinarla dal cestino o premendo "Annulla" sul prossimo avviso.`
    )) return;
    try {
      const res = await api.delete(`/admin/bookings/${booking.id}`);
      const deleted = res.data?.deleted || {};
      const total = Object.values(deleted).reduce((a, b) => a + (Number(b) || 0), 0);
      // Toast with an Undo button that calls the restore endpoint
      toast.success(`${code} spostata nel cestino · ${total} righe rimosse`, {
        duration: 8000,
        action: {
          label: "Annulla",
          onClick: async () => {
            try {
              await api.post(`/admin/bookings/${booking.id}/restore`);
              toast.success(`✓ ${code} ripristinata`);
              refresh();
              refreshTrashCount();
            } catch (e) {
              toast.error(errMsg(e, "Errore ripristino"));
            }
          },
        },
      });
      refresh();
      refreshTrashCount();
    } catch (e) {
      toast.error(errMsg(e, "Errore eliminazione prenotazione"));
    }
  };

  const logout = () => {
    clearSession();
    navigate("/admin/login", { replace: true });
  };

  // Fetch trash count (for showing the badge on the "Cestino" button)
  const refreshTrashCount = useCallback(async () => {
    try {
      const r = await api.get("/admin/bookings/deleted");
      setTrashCount(r.data?.count || 0);
    } catch {
      // silently ignore — the trash button just won't show a badge
    }
  }, []);

  useEffect(() => {
    refreshTrashCount();
  }, [refreshTrashCount]);

  // Open the edit dialog (reusing the same UI as "Nuova prenotazione")
  const openEdit = useCallback(async (booking) => {
    // Lazy-fetch rooms once
    if (editRooms.length === 0) {
      try {
        const r = await api.get("/rooms");
        setEditRooms(r.data || []);
      } catch {
        toast.error("Errore caricamento camere");
        return;
      }
    }
    setEditingBooking(booking);
  }, [editRooms.length]);

  // iter189 — Duplica prenotazione: crea copia via API, ricarica lista
  // e apre subito in modifica la nuova prenotazione così l'host può
  // aggiustare le date, la camera e gli altri dettagli.
  const duplicateBooking = useCallback(async (booking) => {
    if (!booking?.id) return;
    if (!window.confirm(
      `Duplicare la prenotazione ${booking.code}?\n\n` +
      `Verrà creata una copia con le stesse informazioni ma date spostate di +7 giorni ` +
      `(potrai modificarle subito dopo).`
    )) return;
    try {
      const r = await api.post(`/admin/bookings/${booking.id}/duplicate`);
      const nb = r.data;
      toast.success(`Copia creata: ${nb.code}. Modifica ora date e dettagli.`);
      await refresh();
      await openEdit(nb);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la duplicazione");
    }
  }, [refresh, openEdit]);

  // Top extranet-style menu (Booking.com Group Homepage)
  const handleAddProperty = () => {
    setPropDialogOpen(true);
  };

  const topMenu = useMemo(() => {
    // Always shown: Homepage di Gruppo, Dashboard (home), Prenotazioni
    const base = [
      { id: "group-home", label: "Homepage di Gruppo", testId: "top-group-home" },
      { id: "home", label: "Dashboard", testId: "top-dashboard" },
      { id: "bookings", label: "Prenotazioni", testId: "top-bookings" },
    ];
    // Also show the last-visited tab (if different from above)
    if (lastVisited && TOP_MENU_LABELS[lastVisited]) {
      base.push({ id: lastVisited, label: TOP_MENU_LABELS[lastVisited], testId: `top-last-${lastVisited}`, last: true });
    }
    return base;
  }, [lastVisited]);

  // Properties (multi-struttura) — live from backend
  const [properties, setProperties] = useState([]);
  const [activePropertyId, setActivePropertyId] = useState(() => localStorage.getItem("admin_active_property") || "");
  const [propDialogOpen, setPropDialogOpen] = useState(false);
  const [newProp, setNewProp] = useState({ name: "", address: "", color: "#1E3A5F", client_filter_keyword: "" });

  // propertyTone() / findPropertyForKw() sono ora centralizzati in
  // `@/lib/propertyTone` (importati sopra) per consistenza tra tutte le tab.

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/properties");
        const list = r.data?.properties || [];
        setProperties(list);
        if (!activePropertyId && list.length > 0) {
          const def = list.find((p) => p.is_default) || list[0];
          setActivePropertyId(def.id);
          localStorage.setItem("admin_active_property", def.id);
        }
      } catch (e) { console.warn("active property bootstrap failed:", e); }
    })();
  }, []);

  const switchProperty = (p) => {
    setActivePropertyId(p.id);
    localStorage.setItem("admin_active_property", p.id);
    toast.success(`Profilo: ${p.name}`);
    // Hard refresh data: reload the page so every component refetches with new X-Property-Id header
    setTimeout(() => window.location.reload(), 400);
  };

  const createProperty = async () => {
    if (!newProp.name.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      const res = await api.post("/admin/properties", newProp);
      const created = res.data;
      setProperties((prev) => [...prev, created]);
      switchProperty(created);
      setPropDialogOpen(false);
      setNewProp({ name: "", address: "", color: "#1E3A5F", client_filter_keyword: "" });
      toast.success(`${created.name} aggiunta!`);
    } catch (e) {
      toast.error(errMsg(e, "Errore creazione"));
    }
  };

  // === Edit property dialog ===
  const [propEditOpen, setPropEditOpen] = useState(false);
  const [editProp, setEditProp] = useState(null);

  const saveEditProperty = async () => {
    if (!editProp?.name?.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      const payload = {
        name: editProp.name.trim(),
        address: editProp.address || "",
        color: editProp.color || "#AC5D45",
        client_filter_keyword: editProp.client_filter_keyword || "",
      };
      await api.put(`/admin/properties/${editProp.id}`, payload);
      setProperties((prev) => prev.map((p) => (p.id === editProp.id ? { ...p, ...payload } : p)));
      setPropEditOpen(false);
      toast.success(`${payload.name} aggiornata!`);
    } catch (e) {
      toast.error(errMsg(e, "Errore aggiornamento"));
    }
  };

  const deleteEditProperty = async () => {
    if (!editProp?.id) return;
    if (properties.length <= 1) {
      toast.error("Non puoi eliminare l'unica struttura");
      return;
    }
    if (!window.confirm(`Eliminare definitivamente "${editProp.name}"? Le prenotazioni e i dati associati restano in archivio ma non saranno più visibili nel pannello.`)) return;
    try {
      await api.delete(`/admin/properties/${editProp.id}`);
      const remaining = properties.filter((p) => p.id !== editProp.id);
      setProperties(remaining);
      if (activePropertyId === editProp.id && remaining[0]) {
        switchProperty(remaining[0]);
      }
      setPropEditOpen(false);
      toast.success("Struttura eliminata");
    } catch (e) {
      toast.error(errMsg(e, "Errore eliminazione"));
    }
  };

  const activeProperty = activePropertyId === "all"
    ? { id: "all", name: "Tutte le strutture", slug: "all", color: "#1E3A5F" }
    : (properties.find((p) => p.id === activePropertyId) || properties[0] || { name: "B&B Emmanuel", color: "#AC5D45", slug: "emmanuel" });

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-[#1E3A5F] text-white border-b border-[#AC5D45]/30 shadow-sm" data-testid="admin-top-header">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-3">
          {/* B&B name → dropdown property switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid="top-bnb-name"
                className="flex items-center gap-3 flex-shrink-0 group hover:opacity-90 transition cursor-pointer"
              >
                <div
                  className="w-9 h-9 rounded-md flex items-center justify-center text-white shadow-md"
                  style={{ backgroundColor: propertyTone(activeProperty) }}
                >
                  <Building2 className="w-5 h-5" />
                </div>
                <div className="text-left">
                  <h1 className="font-display text-lg leading-none group-hover:text-[#F4D9C9] transition flex items-center gap-1">
                    {activeProperty.name}
                    <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                  </h1>
                  <p className="text-[10px] text-white/70 mt-0.5 group-hover:text-white/90">
                    cambia profilo struttura
                  </p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-white min-w-[280px]" data-testid="top-property-menu">
              <DropdownMenuLabel className="text-xs text-[#635F59] uppercase tracking-wider">
                Profili struttura ({properties.length}/5)
              </DropdownMenuLabel>
              {properties.length > 1 && (
                <DropdownMenuItem
                  onClick={() => switchProperty({ id: "all", name: "Tutte le strutture", slug: "all" })}
                  className="cursor-pointer"
                  data-testid="prop-all"
                >
                  <div className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold mr-2 bg-gradient-to-br from-[#1E3A5F] to-[#AC5D45]">
                    ★
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">Tutte le strutture</div>
                    <div className="text-[10px] text-[#635F59]">Vista combinata · niente filtri</div>
                  </div>
                  {activeProperty.id === "all" && <Check className="w-3.5 h-3.5 ml-1.5 text-emerald-600 shrink-0" />}
                </DropdownMenuItem>
              )}
              {properties.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  onClick={(e) => {
                    // Allow inner "Modifica" button click to bubble first
                    if (e.target.closest("[data-prop-edit]")) return;
                    switchProperty(p);
                  }}
                  className="cursor-pointer"
                  data-testid={`prop-${p.slug}`}
                >
                  <div className="w-6 h-6 rounded-md flex items-center justify-center text-white text-xs font-bold mr-2" style={{ backgroundColor: propertyTone(p) }}>
                    {p.name.charAt(2) || p.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{p.name}</div>
                    {p.address && <div className="text-[10px] text-[#635F59] truncate max-w-[180px]">{p.address}</div>}
                  </div>
                  {p.id === activeProperty.id && <Check className="w-3.5 h-3.5 ml-1.5 text-emerald-600 shrink-0" />}
                  <button
                    type="button"
                    data-prop-edit
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setEditProp({ ...p });
                      setPropEditOpen(true);
                    }}
                    className="ml-1 p-1 rounded hover:bg-[#F5EFE6] text-[#635F59] hover:text-[#1E3A5F]"
                    title="Modifica struttura"
                    data-testid={`prop-edit-${p.slug}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setPropDialogOpen(true)}
                className="cursor-pointer text-[#AC5D45] font-medium"
                data-testid="prop-add"
                disabled={properties.length >= 5}
              >
                <Plus className="w-3.5 h-3.5 mr-2" />
                {properties.length >= 5 ? "Limite raggiunto (5/5)" : "Aggiungi una nuova struttura"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Main menu */}
          <div className="hidden md:flex items-center gap-1 text-sm flex-1 justify-center">
            {topMenu.map((it) => {
              const isActive = activeTab === it.id;
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => setActiveTab(it.id)}
                  data-testid={it.testId}
                  className={`relative px-3.5 py-2 rounded-md transition ${isActive ? "bg-[#AC5D45] text-white font-semibold" : "text-white/90 hover:bg-white/10 hover:text-white"} ${it.last ? "italic opacity-90" : ""}`}
                  title={it.last ? "Ultima sezione consultata" : ""}
                >
                  {it.last && <span className="w-1.5 h-1.5 rounded-full bg-[#AC5D45] inline-block mr-1.5 align-middle" />}
                  {it.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="top-open-site"
              className="inline-flex items-center justify-center h-9 px-3 rounded-md bg-transparent border border-white/40 text-white hover:bg-white/15 transition text-sm"
              title="Apri il sito pubblico in una nuova tab"
            >
              <ExternalLink className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Apri sito</span>
            </a>
            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              data-testid="admin-logout"
              className="bg-transparent border-white/40 text-white hover:bg-white/15 hover:text-white h-9"
            >
              <LogOut className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Esci</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Active property banner — only shown when on a non-default property OR on "all" view */}
      {activeProperty && activeProperty.id === "all" && (
        <div
          className="text-white text-xs py-1.5 text-center font-medium bg-gradient-to-r from-[#1E3A5F] to-[#AC5D45]"
          data-testid="active-property-banner"
        >
          Stai vedendo: <strong>Tutte le strutture</strong> · vista combinata
        </div>
      )}
      {activeProperty && activeProperty.id !== "all" && !activeProperty.is_default && properties.length > 1 && (
        <div
          className="text-white text-xs py-1.5 text-center font-medium"
          style={{ backgroundColor: activeProperty.color || "#AC5D45" }}
          data-testid="active-property-banner"
        >
          Stai gestendo: <strong>{activeProperty.name}</strong>
          {activeProperty.address && <span className="opacity-80 ml-2">· {activeProperty.address}</span>}
        </div>
      )}

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
        {/* Mobile nav toggle */}
        <button
          type="button"
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          className="lg:hidden mb-3 inline-flex items-center gap-2 px-3 py-2 bg-white border border-[#E8E2D9] rounded-lg text-sm text-[#1E3A5F]"
          data-testid="admin-mobile-nav-toggle"
        >
          <Menu className="w-4 h-4" /> {mobileNavOpen ? "Chiudi menu" : "Menu sezioni"}
        </button>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* SIDEBAR */}
          <aside
            className={`lg:w-72 shrink-0 ${mobileNavOpen ? "block" : "hidden"} lg:block`}
            data-testid="admin-sidebar"
          >
            <nav className="bg-white border border-[#E8E2D9] rounded-xl p-3 lg:sticky lg:top-4 space-y-1 max-h-[calc(100vh-2rem)] overflow-y-auto">
              {ADMIN_NAV_GROUPS.map((group) => {
                const isOpen = !!openGroups[group.id];
                // sum notification counts of items in this group (for collapsed badge)
                const groupNotif = group.items.reduce((acc, it) => acc + (it.notifKey ? (notifCounts[it.notifKey] || 0) : 0), 0);
                const hasGroupDot = group.items.some((it) => it.notifKey && sectionHasDot(it.id, notifCounts[it.notifKey]));
                return (
                <div key={group.id}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    data-testid={`group-${group.id}`}
                    className="w-full px-2.5 py-2 text-[11px] uppercase tracking-wider text-[#AC5D45] font-semibold flex items-center gap-2 hover:bg-[#FDFBF7] rounded-md transition group"
                  >
                    <group.icon className="w-3.5 h-3.5" />
                    <span className="flex-1 text-left">{group.label}</span>
                    {!isOpen && hasGroupDot && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-[#AC5D45] inline-block"
                        aria-label="notifiche presenti"
                        data-testid={`group-dot-${group.id}`}
                      />
                    )}
                    {!isOpen && groupNotif > 0 && (
                      <span className="text-[9px] bg-[#AC5D45]/10 text-[#AC5D45] px-1.5 py-0 rounded-full font-bold" data-testid={`group-count-${group.id}`}>{groupNotif}</span>
                    )}
                    {isOpen
                      ? <ChevronDown className="w-3 h-3 text-[#AC5D45]" />
                      : <ChevronRight className="w-3 h-3 text-[#635F59]" />
                    }
                  </button>
                  {isOpen && (
                    <div className="space-y-0.5 mt-0.5 mb-2">
                    {group.items.map((it) => {
                      const Icon = it.icon;
                      const isExternal = !!it.path;
                      const isActive = !isExternal && activeTab === it.id;
                      const onClick = () => {
                        setMobileNavOpen(false);
                        if (isExternal) navigate(it.path);
                        else setActiveTab(it.id);
                      };
                      return (
                        <button
                          key={it.id}
                          type="button"
                          onClick={onClick}
                          data-testid={`nav-${it.id}`}
                          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] text-left transition relative ${
                            isActive
                              ? "bg-[#1E3A5F] text-white"
                              : "text-[#2D2926] hover:bg-[#FDFBF7]"
                          }`}
                        >
                          <div className="relative">
                            <Icon className={`w-4 h-4 ${isActive ? "text-white" : "text-[#635F59]"}`} />
                            {it.notifKey && sectionHasDot(it.id, notifCounts[it.notifKey]) && (
                              <span
                                className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[#AC5D45] ring-2 ring-white"
                                data-testid={`nav-dot-${it.id}`}
                                aria-label="notifica"
                              />
                            )}
                          </div>
                          <span className="flex-1">{it.label}</span>
                          {it.notifKey && notifCounts[it.notifKey] > 0 && sectionHasDot(it.id, notifCounts[it.notifKey]) && (
                            <Badge
                              variant="outline"
                              className={`text-[9px] h-4 px-1.5 ${isActive ? "bg-white/20 text-white border-white/30" : "bg-[#AC5D45]/10 text-[#AC5D45] border-[#AC5D45]/30"}`}
                              data-testid={`nav-count-${it.id}`}
                            >
                              {notifCounts[it.notifKey]}
                            </Badge>
                          )}
                          {isExternal && <ChevronRight className="w-3.5 h-3.5 opacity-60" />}
                        </button>
                      );
                    })}
                    </div>
                  )}
                </div>
                );
              })}
            </nav>
          </aside>

          {/* CONTENT AREA */}
          <section className="flex-1 min-w-0 space-y-4" data-testid="admin-content">
            {/* GLOBAL sync alerts (walk-in webhook ambiguities) */}
            <SyncAlertsBanner />
            {/* HOME — overview cards */}
            {activeTab === "home" && (
              <HomeDashboard
                navigate={navigate}
                setActiveTab={setActiveTab}
                stats={stats}
                activeProperty={activeProperty}
                notifications={notifications}
                markViewedAndGo={markViewedAndGo}
                setFilterDate={setFilterDate}
                setFilterDateMode={setFilterDateMode}
                setFilterStatus={setFilterStatus}
                setFilterReceipt={setFilterReceipt}
                setSortBy={setSortBy}
                bookings={bookings}
              />
            )}

            {/* BOOKINGS TAB */}
            {activeTab === "bookings" && (
              <>
                <PendingBookingsBanner
                  bookings={bookings}
                  currentFilterStatus={filterStatus}
                  onGoToPending={() => {
                    setFilterDate("");
                    setFilterDateMode("stay");
                    setFilterStatus("pending");
                    setFilterReceipt("all");
                    setHidePast(false);
                    // Scroll to list top so the user immediately sees the pending row(s)
                    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
                  }}
                />
                {/* iter186 — Banner "totali gruppo" e "acconti superiori" RIMOSSI
                    su richiesta dell'host: creavano confusione e apparivano
                    anche dopo la correzione dei totali. Le stesse informazioni
                    restano visibili come chip inline sulle singole prenotazioni. */}
                <BookingsPanel
                  bookings={bookings}
                  filteredBookings={filteredBookings}
                  loading={loading}
                  t={t}
                  search={search} setSearch={setSearch}
                  filterDate={filterDate} setFilterDate={setFilterDate}
                  filterDateMode={filterDateMode} setFilterDateMode={setFilterDateMode}
                  filterStatus={filterStatus} setFilterStatus={setFilterStatus}
                  filterReceipt={filterReceipt} setFilterReceipt={setFilterReceipt}
                  hidePast={hidePast} setHidePast={setHidePast}
                  sortBy={sortBy} setSortBy={setSortBy}
                  setSelected={setSelected}
                  selected={selected}
                  updateStatus={updateStatus}
                  deleteBooking={deleteBooking}
                  openEdit={openEdit}
                  duplicateBooking={duplicateBooking}
                  onOpenOtaSync={() => setOtaSyncOpen(true)}
                  onOpenTrash={() => setTrashOpen(true)}
                  trashCount={trashCount}
                  onMergeDone={refresh}
                />
              </>
            )}

            {/* OTA Sync dialog — global to Prenotazioni tab */}
            <OtaSyncDialog
              open={otaSyncOpen}
              onOpenChange={setOtaSyncOpen}
              onApplied={refresh}
            />

            {/* Trash dialog — soft-deleted bookings with 7-day recovery */}
            <TrashDialog
              open={trashOpen}
              onOpenChange={setTrashOpen}
              onRestored={() => { refresh(); refreshTrashCount(); }}
            />

            {/* Edit booking dialog — reuses NewBookingDialog in edit mode.
                When a booking is changed, refresh both the list and the trash count. */}
            <NewBookingDialog
              open={!!editingBooking}
              onOpenChange={(v) => { if (!v) setEditingBooking(null); }}
              date=""
              rooms={editRooms}
              editingBooking={editingBooking}
              onCreated={() => { setEditingBooking(null); refresh(); }}
            />

            {activeTab === "messages" && (
              <div className="space-y-3">
                <div className="bg-white border border-[#E8E2D9] rounded-xl p-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 text-sm text-[#635F59]">
                    <strong className="text-[#1E3A5F]">Centro Messaggi Ospiti</strong> — form contatto, note ospiti, cancellazioni, richieste flessibili.
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const r = await api.post("/admin/messages/mark-all-read");
                        setNotifCounts((prev) => ({ ...prev, messages: 0 }));
                        toast.success(`${r.data?.marked_read || 0} messaggi marcati come letti`);
                      } catch (err) {
                        toast.error("Errore");
                        console.warn("[messages] mark-all-read failed:", err);
                      }
                    }}
                    className="text-xs px-3 py-1.5 rounded border border-[#E8E2D9] hover:bg-stone-50 text-[#1E3A5F]"
                    data-testid="msg-mark-all-read"
                  >
                    ✓ Segna tutti come letti
                  </button>
                  <label className="inline-flex items-center gap-2 text-sm text-[#1E3A5F] cursor-pointer" data-testid="msg-relevant-toggle">
                    <input
                      type="checkbox"
                      checked={messagesRelevantOnly}
                      onChange={async (e) => {
                        const v = e.target.checked;
                        setMessagesRelevantOnly(v);
                        try {
                          const r = await api.get(v ? "/admin/messages?relevant_only=true" : "/admin/messages");
                          setMessages(r.data || []);
                        } catch (err) {
                          console.warn("[messages] toggle relevant filter failed:", err);
                        }
                      }}
                      className="h-4 w-4 accent-[#AC5D45]"
                    />
                    Solo rilevanti (legati a prenotazioni)
                  </label>
                </div>
                <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden">
                {loading ? (
                  <div className="p-10 text-center text-[#635F59]">{t.common.loading}</div>
                ) : messages.length === 0 ? (
                  <div className="p-10 text-center text-[#635F59]">{t.admin.no_messages}</div>
                ) : (
                  <div className="divide-y divide-[#E8E2D9]" data-testid="messages-list">
                    {messages.map((m) => {
                      const phone = m.phone || extractPhone(m.message);
                      // Extract check-in/check-out from subject if it's a flexible request
                      let ci = null, co = null;
                      if (m.kind === "flexible_request") {
                        const sm = /(\d{4}-\d{2}-\d{2})\s*→\s*(\d{4}-\d{2}-\d{2})/.exec(m.subject || "");
                        if (sm) { ci = sm[1]; co = sm[2]; }
                      }
                      const firstName = (m.name || "").trim().split(/\s+/)[0];
                      const wa = buildWhatsAppLink(phone, waMessageForFlexibleRequest(firstName, ci, co));
                      const mb = m.matched_booking;
                      const kindMeta = {
                        guest_note: { label: "Nota ospite", color: "bg-amber-100 text-amber-800 border-amber-300" },
                        cancellation: { label: "Cancellazione", color: "bg-rose-100 text-rose-800 border-rose-300" },
                        flexible_request: { label: "Richiesta flessibile", color: "bg-violet-100 text-violet-800 border-violet-300" },
                        shuttle_request: { label: "Navetta", color: "bg-sky-100 text-sky-800 border-sky-300" },
                      }[m.kind] || null;
                      return (
                        <div key={m.id} className="p-5" data-testid={`message-${m.id}`}>
                          <div className="flex items-start justify-between gap-4 mb-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-semibold text-[#1E3A5F]">{m.name} <span className="font-normal text-[#635F59] text-sm">· {m.email}</span></p>
                                {kindMeta && (
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold uppercase ${kindMeta.color}`}>{kindMeta.label}</span>
                                )}
                                {mb && (
                                  <a
                                    href={`/admin/dashboard?tab=bookings&code=${mb.code}`}
                                    className="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-100 text-emerald-800 border-emerald-300 font-mono hover:underline"
                                    data-testid={`msg-booking-link-${mb.code}`}
                                    title={`${mb.room_name || ""} · ${mb.check_in || ""}→${mb.check_out || ""} · ${mb.status}`}
                                  >
                                    🛏 {mb.code}
                                  </a>
                                )}
                              </div>
                              <p className="text-sm text-[#AC5D45] mt-1">{m.subject}</p>
                              {phone && <p className="text-xs text-[#635F59] mt-1">📞 {phone}</p>}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {wa && (
                                <a
                                  href={wa}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded bg-[#25D366] hover:bg-[#1FBA56] text-white text-xs font-medium"
                                  title={`Rispondi a ${firstName || m.name} su WhatsApp`}
                                  data-testid={`msg-wa-${m.id}`}
                                >
                                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                </a>
                              )}
                              <span className="text-xs text-[#635F59] whitespace-nowrap">{formatDateTime(m.created_at)}</span>
                            </div>
                          </div>
                          <p className="text-sm text-[#2D2926] leading-relaxed whitespace-pre-wrap">{m.message}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>
            )}

            {activeTab === "kb" && <KnowledgeBaseTab />}
            {activeTab === "rooms" && <RoomManagementTab />}
            {activeTab === "content" && <SiteContentTab />}
            {activeTab === "promos" && <PromoSettingsTab />}
            {activeTab === "rate-plans" && <RatePlansTab />}
            {activeTab === "rms" && <RmsTab />}
            {activeTab === "finance-import" && <FinanceImportTab />}
            {activeTab === "payment-methods" && <PaymentMethodsTab />}
            {activeTab === "guide" && <GuideCalendarTab />}
            {activeTab === "calendar" && (
              <CalendarTab
                onOpenBooking={(b) => {
                  setActiveTab("bookings");
                  setFilterStatus("all");
                  setFilterDate("");
                  setFilterReceipt("all");
                  setSearch(b?.code || "");
                }}
              />
            )}
            {activeTab === "ical-inbox" && (
              <IcalInboxTab
                onCreateBookingFromIcal={(prefill) => {
                  // iter186 — passa i dati alla NewBookingDialog via
                  // sessionStorage. CalendarTab al mount rileva la chiave e
                  // apre il form pre-riempito con room + date.
                  try {
                    sessionStorage.setItem("icalPrefill", JSON.stringify(prefill));
                  } catch { /* noop */ }
                  setActiveTab("calendar");
                }}
              />
            )}
            {activeTab === "booking-history" && <BookingHistoryTab />}
            {activeTab === "cleaning-plan" && <CleaningPlanTab />}

            {activeTab === "checkins" && <CheckinsTab />}
            {activeTab === "api-logs" && <ApiLogsTab />}
            {activeTab === "profile" && <ProfileTab />}
            {activeTab === "compliance" && (
              <ComplianceTab
                onGoToBookings={(dateFrom) => {
                  setFilterDate(dateFrom || "");
                  setFilterStatus("confirmed");
                  setFilterReceipt("all");
                  setActiveTab("bookings");
                }}
              />
            )}
            {activeTab === "management" && <ManagementTab />}
            {activeTab === "employees" && <EmployeesTab />}
            {activeTab === "export" && <ExportTab />}
            {activeTab === "customers" && <CustomersTab />}
            {activeTab === "shuttle" && <ShuttleApprovalsTab />}
            {activeTab === "reviews" && <ReviewsTab />}
            {activeTab === "group-home" && <GroupHomeTab onAddProperty={handleAddProperty} />}
            {activeTab === "deadlines" && <DeadlinesTab />}
            {activeTab === "contracts" && <ContractsTab />}
            {activeTab === "guide-app" && <GuideIntegrationTab />}
            {activeTab === "receipts-quotes" && <ReceiptsQuotesTab />}
            {activeTab === "regulations" && <RegulationsTab />}
            {activeTab === "photo-library" && <PhotoLibraryTab />}
          </section>
        </div>
      </main>

      {/* ===== Add property dialog (multi-struttura) ===== */}
      <Dialog open={propDialogOpen} onOpenChange={setPropDialogOpen}>
        <DialogContent data-testid="add-property-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1E3A5F]">Aggiungi una nuova struttura</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#635F59]">Nome struttura *</label>
              <Input
                value={newProp.name}
                onChange={(e) => setNewProp({ ...newProp, name: e.target.value, client_filter_keyword: newProp.client_filter_keyword || e.target.value.split(" ").pop() })}
                placeholder="Es. B&B Gabbiano"
                data-testid="new-prop-name"
              />
            </div>
            <div>
              <label className="text-xs text-[#635F59]">Indirizzo</label>
              <Input
                value={newProp.address}
                onChange={(e) => setNewProp({ ...newProp, address: e.target.value })}
                placeholder="Via... Città"
                data-testid="new-prop-address"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#635F59]">Colore profilo</label>
                <div className="flex gap-1.5 mt-1 flex-wrap">
                  {["#AC5D45", "#1E3A5F", "#5E8B7E", "#8E44AD", "#D35400", "#16A085"].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewProp({ ...newProp, color: c })}
                      style={{ backgroundColor: c }}
                      className={`w-8 h-8 rounded-full ring-2 transition ${newProp.color === c ? "ring-[#1E3A5F] scale-110" : "ring-transparent"}`}
                      data-testid={`new-prop-color-${c.slice(1)}`}
                    />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-[#635F59]">Parola chiave filtro timbrature</label>
                <Input
                  value={newProp.client_filter_keyword}
                  onChange={(e) => setNewProp({ ...newProp, client_filter_keyword: e.target.value })}
                  placeholder="Es. Gabbiano"
                  data-testid="new-prop-keyword"
                />
                <p className="text-[10px] text-[#635F59] mt-1">Per riconoscere automaticamente le timbrature di questa struttura nei file Excel</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#E8E2D9]">
            <Button variant="outline" onClick={() => setPropDialogOpen(false)}>Annulla</Button>
            <Button onClick={createProperty} data-testid="new-prop-create" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
              <Plus className="w-4 h-4 mr-1" />Crea struttura
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Edit property dialog ===== */}
      <Dialog open={propEditOpen} onOpenChange={setPropEditOpen}>
        <DialogContent data-testid="edit-property-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#1E3A5F]">Modifica struttura</DialogTitle>
          </DialogHeader>
          {editProp && (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[#635F59]">Nome struttura *</label>
                <Input
                  value={editProp.name || ""}
                  onChange={(e) => setEditProp({ ...editProp, name: e.target.value })}
                  data-testid="edit-prop-name"
                />
              </div>
              <div>
                <label className="text-xs text-[#635F59]">Indirizzo</label>
                <Input
                  value={editProp.address || ""}
                  onChange={(e) => setEditProp({ ...editProp, address: e.target.value })}
                  data-testid="edit-prop-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#635F59]">Colore profilo</label>
                  <div className="flex gap-1.5 mt-1 flex-wrap">
                    {["#EA580C", "#38BDF8", "#16A34A", "#AC5D45", "#1E3A5F", "#8E44AD"].map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setEditProp({ ...editProp, color: c })}
                        style={{ backgroundColor: c }}
                        className={`w-8 h-8 rounded-md ring-2 transition ${editProp.color === c ? "ring-[#1E3A5F] scale-110" : "ring-transparent"}`}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-[#635F59]">Parola chiave filtro</label>
                  <Input
                    value={editProp.client_filter_keyword || ""}
                    onChange={(e) => setEditProp({ ...editProp, client_filter_keyword: e.target.value })}
                    data-testid="edit-prop-keyword"
                  />
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-between gap-2 pt-3 border-t border-[#E8E2D9]">
            <Button variant="outline" onClick={deleteEditProperty} className="text-rose-700 border-rose-300 hover:bg-rose-50" data-testid="edit-prop-delete">
              <Trash2 className="w-4 h-4 mr-1" /> Elimina
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPropEditOpen(false)}>Annulla</Button>
              <Button onClick={saveEditProperty} data-testid="edit-prop-save" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">
                Salva modifiche
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Bookings list with filters (extracted out of the main switch for clarity)
const BookingsPanel = ({
  bookings, filteredBookings, loading, t,
  search, setSearch, filterDate, setFilterDate,
  filterDateMode, setFilterDateMode,  filterStatus, setFilterStatus, filterReceipt, setFilterReceipt,
  hidePast, setHidePast,
  sortBy, setSortBy, setSelected, selected, updateStatus, deleteBooking, openEdit, duplicateBooking,
  onOpenOtaSync, onOpenTrash, trashCount, onMergeDone,
}) => {
  // Selection is keyed by `id` (always unique) NOT by `code` — Booking
  // sometimes recreates a record with the same external code (stub iCal +
  // XLS import); selecting by code would highlight ALL rows sharing it.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [merging, setMerging] = useState(false);

  // Identify duplicate booking codes so the UI can flag them and the host
  // can quickly decide which pair to merge.
  const duplicateCodes = useMemo(() => {
    const counts = new Map();
    for (const b of filteredBookings) counts.set(b.code, (counts.get(b.code) || 0) + 1);
    const dups = new Set();
    for (const [c, n] of counts) if (n > 1) dups.add(c);
    return dups;
  }, [filteredBookings]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelectedIds(new Set());

  const mergeSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length < 2) {
      toast.error("Seleziona almeno 2 prenotazioni da unire");
      return;
    }
    if (!window.confirm(`Unire ${ids.length} prenotazioni in una sola?\n\nVerranno COMBINATI i dati di tutte (nome, prezzo, camera, anagrafica): se una ha il nome e l'altra il prezzo, il risultato finale avrà entrambi. Le righe duplicate finiranno nel cestino (recuperabili 7 giorni).`)) {
      return;
    }
    setMerging(true);
    try {
      // Use id-based endpoint so duplicates sharing the same booking code
      // (Booking stub + email) can be merged. The legacy /merge-selected
      // would dedupe them and refuse.
      const { data } = await api.post("/admin/bookings/merge-selected-ids", { ids });
      toast.success(`Unite ${data.merged_count + 1} prenotazioni → ${data.winner}`);
      clearSelected();
      onMergeDone?.();
    } catch (err) {
      toast.error(errMsg(err, "Unione fallita"));
    } finally {
      setMerging(false);
    }
  };

  return (
  <>
    {/* Top action bar: "Verifica e sincronizza" (OTA) + "Cestino" (trash with 7-day recovery) */}
    <div className="flex justify-end gap-2 mb-2">
      {onOpenTrash && (
        <Button
          onClick={onOpenTrash}
          variant="outline"
          size="sm"
          className="text-rose-700 border-rose-300 hover:bg-rose-50 relative"
          title="Cestino prenotazioni eliminate (recuperabili per 7 giorni)"
          data-testid="bookings-open-trash"
        >
          <Trash2 className="w-4 h-4 mr-1" />
          Cestino
          {trashCount > 0 && (
            <span className="ml-1.5 bg-rose-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
              {trashCount}
            </span>
          )}
        </Button>
      )}
      {onOpenOtaSync && (
        <Button
          onClick={onOpenOtaSync}
          variant="outline"
          size="sm"
          className="text-[#1E3A5F] border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white"
          title="Carica statement Booking/Airbnb per correggere prezzi reali incassati"
          data-testid="bookings-open-ota-sync"
        >
          <FileSpreadsheet className="w-4 h-4 mr-1" />
          Verifica e sincronizza (OTA)
        </Button>
      )}
    </div>
    {/* Quick filter chips with live counters */}
    {(() => {
      const today = new Date().toISOString().slice(0, 10);
      const cPending = bookings.filter((b) => b.status === "pending").length;
      const cConfirmed = bookings.filter((b) => b.status === "confirmed").length;
      // "Check-in fatto" — allineato ESATTAMENTE al tab Check-in
      // (CheckinsTab.jsx righe 99-104): include status "checked_in",
      // "completed" e ingressi walk-in via App Guida.
      const cCheckedIn = bookings.filter(
        (b) => b.status === "checked_in" || b.status === "completed" || b.source === "walk_in"
      ).length;
      const cCompleted = bookings.filter((b) => b.status === "completed").length;
      const cToday = bookings.filter((b) => b.check_in === today && ["confirmed", "checked_in", "pending"].includes(b.status)).length;
      const cCheckoutToday = bookings.filter((b) => b.check_out === today && ["confirmed", "completed", "checked_in"].includes(b.status)).length;
      const cNeedsAssignment = bookings.filter((b) => b.needs_room_assignment === true || b.room_id === "tbd_walkin").length;
      const Chip = ({ id, label, count, tone, icon: Icon, onClick }) => {
        const active = filterStatus === id || (id === "all" && filterStatus === "all");
        const tones = {
          slate: active ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
          amber: active ? "bg-amber-500 text-white border-amber-500" : "bg-white text-amber-700 border-amber-300 hover:bg-amber-50",
          yellow: active ? "bg-yellow-500 text-white border-yellow-500" : "bg-white text-yellow-700 border-yellow-300 hover:bg-yellow-50",
          emerald: active ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50",
          orange: active ? "bg-orange-500 text-white border-orange-500" : "bg-white text-orange-700 border-orange-300 hover:bg-orange-50",
          violet: active ? "bg-violet-600 text-white border-violet-600" : "bg-white text-violet-700 border-violet-300 hover:bg-violet-50",
          sky: active ? "bg-sky-600 text-white border-sky-600" : "bg-white text-sky-700 border-sky-300 hover:bg-sky-50",
          terracotta: active ? "bg-[#AC5D45] text-white border-[#AC5D45]" : "bg-white text-[#AC5D45] border-[#AC5D45]/30 hover:bg-[#AC5D45]/10",
        };
        return (
          <button
            type="button"
            onClick={onClick || (() => { setFilterStatus(id); setFilterDate(""); setFilterDateMode("stay"); })}
            className={`text-xs font-semibold px-3 py-2 rounded-md border transition flex items-center justify-center gap-1.5 min-w-[120px] whitespace-nowrap ${tones[tone]}`}
            data-testid={`chip-${id}`}
          >
            {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
            <span className="flex-1 text-left">{label}</span>
            {count != null && (
              <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${active ? "bg-white/25" : "bg-current/10"}`}>
                {count}
              </span>
            )}
          </button>
        );
      };
      return (
        <div className="bg-white border border-[#E8E2D9] rounded-xl p-3 mb-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-1.5" data-testid="bookings-chips">
          <Chip id="all" label="Tutte" count={bookings.length} tone="slate" icon={ListChecks} />
          <Chip id="needs_assignment" label="Da assegnare" count={cNeedsAssignment} tone="amber" icon={BedDouble} />
          <Chip id="pending" label="In attesa" count={cPending} tone="yellow" icon={Clock} />
          <Chip id="confirmed" label="Confermate" count={cConfirmed} tone="emerald" icon={Check} />
          <Chip id="checked_in" label="Check-in fatto" count={cCheckedIn} tone="orange" icon={LogIn} />
          <Chip id="completed" label="Pagate" count={cCompleted} tone="violet" icon={Wallet} />
          <Chip
            id="today-checkin"
            label="Check-in oggi"
            count={cToday}
            tone="terracotta"
            icon={CalendarDays}
            onClick={() => { setFilterStatus("all"); setFilterDateMode("checkin"); setFilterDate(today); }}
          />
          <Chip
            id="checkout-today"
            label="Check-out oggi"
            count={cCheckoutToday}
            tone="sky"
            icon={LogOutIcon}
            onClick={() => { setFilterStatus("all"); setFilterDateMode("checkout"); setFilterDate(today); }}
          />
        </div>
      );
    })()}
    <div className="bg-white border border-[#E8E2D9] rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-center" data-testid="bookings-filters">
      <div className="md:col-span-4 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#635F59]" />
        <Input placeholder="Cerca per codice, nome o email..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="filter-search" className="pl-9 h-10" />
      </div>
      <div className="md:col-span-3 relative">
        <CalendarSearch className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#635F59] pointer-events-none" />
        <DateInputIT value={filterDate} onChange={(e) => { setFilterDate(e.target.value); setFilterDateMode("stay"); }} data-testid="filter-date" className="pl-9 h-10 pr-16" />
        {/* iter189 — bottone "Oggi" per filtrare in 1 tap arrivi/partenze/in-house della giornata */}
        <button
          type="button"
          onClick={() => { setFilterDate(todayISO()); setFilterDateMode("stay"); }}
          className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold px-2 py-1 rounded transition-colors ${filterDate === todayISO() ? "bg-[#AC5D45] text-white" : "bg-[#F2EEE6] text-[#1E3A5F] hover:bg-[#E8E2D9]"}`}
          data-testid="filter-today-btn"
          title="Filtra prenotazioni che coprono la giornata di oggi (arrivi + in-house + partenze)"
        >
          Oggi
        </button>
        {filterDate && filterDateMode !== "stay" && (
          <div className="absolute -bottom-5 left-0 text-[10px] text-[#AC5D45] font-semibold">
            Filtro: {filterDateMode === "checkin" ? "check-in" : "check-out"} = {filterDate}
          </div>
        )}
      </div>
      <div className="md:col-span-3">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-10" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="needs_assignment">🛏 Camere da assegnare</SelectItem>
            <SelectItem value="pending">In attesa</SelectItem>
            <SelectItem value="confirmed">Confermate</SelectItem>
            <SelectItem value="checked_in">Check-in fatto · Walk-in</SelectItem>
            <SelectItem value="completed">Pagate</SelectItem>
            <SelectItem value="rejected">Rifiutate</SelectItem>
            <SelectItem value="cancelled">Annullate</SelectItem>
            <SelectItem value="frozen">Congelate</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Select value={filterReceipt} onValueChange={setFilterReceipt}>
          <SelectTrigger className="h-10" data-testid="filter-receipt"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le ricevute</SelectItem>
            <SelectItem value="to_verify">Da verificare</SelectItem>
            <SelectItem value="with">Con ricevuta</SelectItem>
            <SelectItem value="without">Senza ricevuta</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-12 md:flex md:items-center md:gap-3 -mt-1">
        <div className="flex-1">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="h-10" data-testid="filter-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="created_desc">Più recenti (per data inserimento)</SelectItem>
              <SelectItem value="created_asc">Più vecchie (per data inserimento)</SelectItem>
              <SelectItem value="checkin_asc">Soggiorno: prima il più vicino ↑</SelectItem>
              <SelectItem value="checkin_desc">Soggiorno: prima il più lontano ↓</SelectItem>
              <SelectItem value="total_desc">Totale ↓</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <button
          type="button"
          data-testid="toggle-hide-past"
          onClick={() => setHidePast((v) => !v)}
          title={hidePast
            ? "Vengono nascoste le prenotazioni già terminate. Clicca per mostrarle."
            : "Vengono mostrate tutte le prenotazioni. Clicca per nascondere quelle già terminate."}
          className={`mt-2 md:mt-0 h-10 px-4 rounded-full text-sm font-medium border transition-colors flex items-center gap-2 whitespace-nowrap ${
            hidePast
              ? "bg-[#1E3A5F] text-white border-[#1E3A5F] hover:bg-[#16324f]"
              : "bg-white text-[#635F59] border-[#E8E2D9] hover:bg-[#FDFBF7]"
          }`}
        >
          <span className={`inline-block w-2 h-2 rounded-full ${hidePast ? "bg-emerald-400" : "bg-[#C9C3B8]"}`} />
          {hidePast ? "Solo attuali e future" : "Anche già terminate"}
        </button>
      </div>
      <div className="md:col-span-12 text-xs text-[#635F59] flex items-center justify-between" data-testid="filter-count">
        <span>
          {filteredBookings.length} di {bookings.length} prenotazioni
          {hidePast && !search.trim() && (<> · <span className="text-[#1E3A5F]">nascoste quelle già terminate</span></>)}
          {hidePast && search.trim() && (<> · <span className="text-[#AC5D45]">ricerca su tutto lo storico</span></>)}
          {filterDate && (<> · giorno <strong className="text-[#1E3A5F]">{formatDate(filterDate)}</strong></>)}
        </span>
        {(filterStatus !== "all" || filterReceipt !== "all" || search.trim() || filterDate || !hidePast) && (
          <button type="button" onClick={() => { setFilterStatus("all"); setFilterReceipt("all"); setSearch(""); setFilterDate(""); setFilterDateMode("stay"); setHidePast(true); }} data-testid="clear-filters" className="text-[#AC5D45] hover:underline">
            Pulisci filtri
          </button>
        )}
      </div>
    </div>

    <div className="bg-white border border-[#E8E2D9] rounded-xl overflow-hidden">
      {selectedIds.size > 0 && (
        <div className="px-3 py-2 bg-sky-50 border-b border-sky-200 flex items-center justify-between flex-wrap gap-2" data-testid="merge-bar">
          <span className="text-sm text-sky-900 font-medium">
            ☑ <strong>{selectedIds.size}</strong> selezionate
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelected}
              data-testid="merge-clear-selection"
            >
              Annulla
            </Button>
            <Button
              size="sm"
              onClick={mergeSelected}
              disabled={merging || selectedIds.size < 2}
              className="bg-sky-700 hover:bg-sky-800 text-white"
              data-testid="merge-selected-btn"
              title={selectedIds.size < 2 ? "Seleziona almeno 2 prenotazioni" : "Unisci le prenotazioni selezionate in una sola"}
            >
              {merging ? "Unione…" : `Unisci ${selectedIds.size} selezionate`}
            </Button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="p-10 text-center text-[#635F59]">{t.common.loading}</div>
      ) : filteredBookings.length === 0 ? (
        <div className="p-10 text-center text-[#635F59]" data-testid="no-bookings-filtered">
          {bookings.length === 0 ? t.admin.no_bookings : "Nessuna prenotazione corrisponde ai filtri."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="px-3 py-2 border-b border-[#E8E2D9] bg-[#FDFBF7]/60">
            <BookingStatusLegend
              showWalkinDot={filteredBookings.some((b) => b.source === "walk_in")}
              showEstimatedPrice={filteredBookings.some((b) => b.price_estimated)}
            />
          </div>
          <Table data-testid="bookings-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-9 px-2">
                  <input
                    type="checkbox"
                    className="w-4 h-4 cursor-pointer accent-sky-600"
                    title="Seleziona tutte le prenotazioni filtrate"
                    aria-label="Seleziona tutte"
                    checked={filteredBookings.length > 0 && filteredBookings.every((b) => selectedIds.has(b.id))}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(new Set(filteredBookings.map((b) => b.id)));
                      } else {
                        clearSelected();
                      }
                    }}
                    data-testid="select-all-bookings"
                  />
                </TableHead>
                <TableHead>{t.admin.code}</TableHead>
                <TableHead>{t.admin.guest}</TableHead>
                <TableHead>{t.admin.room}</TableHead>
                <TableHead className="whitespace-nowrap">{t.admin.dates}</TableHead>
                <TableHead className="text-right whitespace-nowrap">{t.admin.total_col}</TableHead>
                <TableHead className="text-center">{t.admin.status}</TableHead>
                <TableHead className="text-right">{t.admin.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBookings.map((b) => (
                <TableRow
                  key={b.id}
                  data-testid={`booking-row-${b.code}`}
                  className={selectedIds.has(b.id) ? "bg-sky-50/60" : ""}
                >
                  <TableCell className="w-9 px-2">
                    <input
                      type="checkbox"
                      className="w-4 h-4 cursor-pointer accent-sky-600"
                      checked={selectedIds.has(b.id)}
                      onChange={() => toggleSelected(b.id)}
                      aria-label={`Seleziona ${b.code}`}
                      data-testid={`select-${b.code}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono font-semibold text-[#AC5D45]">
                    {b.code}
                    {duplicateCodes.has(b.code) && (
                      <div className="text-[9px] uppercase font-bold bg-rose-600 text-white px-1.5 py-0.5 rounded mt-0.5 inline-block" title="Stesso codice presente in più righe. Selezionale e clicca Unisci.">
                        ⚠️ DUPLICATO
                      </div>
                    )}
                    {b.group_code && (
                      <div className="text-[9px] uppercase font-bold bg-violet-600 text-white px-1 py-0.5 rounded mt-0.5 inline-block" title={`Gruppo ${b.group_code} · camera ${b.group_index || "?"}/${b.group_size || "?"}`}>
                        GRUPPO · {b.group_index || "?"}/{b.group_size || "?"}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium flex items-center gap-1.5">
                      {b.source === "walk_in" && (
                        <span
                          className="inline-block w-2 h-2 rounded-full bg-amber-500 shrink-0"
                          title="Walk-in dalla Guida Digitale — classifica la provenienza per rimuovere l'indicatore"
                          data-testid="walkin-dot"
                        />
                      )}
                      {(() => {
                        // iter170: iCal orphans from Booking.com often have
                        // `.` or empty as first/last name. Show a highlighted
                        // "Compila anagrafica" CTA so the host can open the
                        // edit modal in one tap and fill the guest data.
                        const fn = (b.guest?.first_name || "").trim();
                        const ln = (b.guest?.last_name || "").trim();
                        const isOrphan = (!fn && !ln) || (fn === "." && !ln) || (fn === "-" && !ln);
                        if (isOrphan) {
                          return (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openEdit && openEdit(b); }}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 rounded px-1.5 py-0.5"
                              data-testid={`fill-guest-${b.code}`}
                              title="Prenotazione iCal senza nome ospite — clicca per compilare"
                            >
                              <FileEdit className="w-3 h-3" />
                              Compila anagrafica
                            </button>
                          );
                        }
                        return <span>{fn} {ln}</span>;
                      })()}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-xs text-[#635F59] truncate">{b.guest.email}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {b.needs_room_assignment === true || b.room_id === "tbd_walkin" ? (
                      <span className="text-amber-700 font-semibold">⚠️ Da assegnare</span>
                    ) : (
                      <span>
                        {b.guide_room_number != null && (
                          <span className="bg-[#1E3A5F] text-white text-[10px] font-mono font-bold px-1.5 py-0.5 rounded mr-1.5" title="Numero camera (App Guida)">
                            #{b.guide_room_number}
                          </span>
                        )}
                        {b.room_name}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    <div>{formatDate(b.check_in)}</div>
                    <div className="text-[#635F59]">→ {formatDate(b.check_out)}</div>
                  </TableCell>
                  <TableCell className="font-semibold whitespace-nowrap">
                    {(() => {
                      // Group bookings: only the master row (group_index=1)
                      // displays the full stay total. The other rooms show
                      // a dash with a hint pointing at the master — the
                      // host explicitly asked for this so she doesn't read
                      // 3× €160 as "€480 due three times".
                      const isGroup = !!b.group_code && (b.group_size || 1) > 1;
                      const isSubGroup = isGroup && (b.group_index || 1) > 1;
                      if (isSubGroup) {
                        return (
                          <span
                            className="inline-flex items-center gap-1 text-[11px] font-medium bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5"
                            title={`Pagamento sulla camera 1/${b.group_size} del gruppo ${b.group_code}`}
                            data-testid={`group-sub-no-total-${b.code}`}
                          >
                            — su #1/{b.group_size}
                          </span>
                        );
                      }
                      const displayTotal = isGroup
                        ? Number(b.group_total ?? b.total ?? 0)
                        : Number(b.total ?? 0);
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span data-testid={`total-${b.code}`} className="inline-flex items-center gap-1">
                            €{displayTotal.toFixed(2)}
                            {b.price_estimated && (
                              <span
                                className="text-amber-600 text-[11px] leading-none"
                                title="Prezzo stimato — Booking.com non invia più il totale nelle email di notifica. Verifica il valore reale sull'extranet Booking.com o modifica manualmente."
                                data-testid={`price-estimated-${b.code}`}
                              >
                                ⚠
                              </span>
                            )}
                          </span>
                          {isGroup && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5 w-fit"
                              title={`Totale di gruppo (${b.group_size} camere)`}
                            >
                              gruppo · {b.group_size} camere
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Select
                      value={b.status}
                      onValueChange={(newStatus) => {
                        if (newStatus && newStatus !== b.status) updateStatus(b.id, newStatus);
                      }}
                    >
                      <SelectTrigger
                        className={`h-7 px-2 py-0 text-[11px] font-semibold border rounded-md w-auto min-w-[120px] focus:ring-1 focus:ring-offset-0 ${colorFor(b.status)}`}
                        data-testid={`status-select-${b.code}`}
                        title="Clicca per cambiare stato"
                      >
                        <SelectValue>{labelFor(b.status)}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">{labelFor("pending")}</SelectItem>
                        <SelectItem value="confirmed">{labelFor("confirmed")}</SelectItem>
                        <SelectItem value="checked_in">{labelFor("checked_in")}</SelectItem>
                        <SelectItem value="completed">{labelFor("completed")}</SelectItem>
                        <SelectItem value="frozen">{labelFor("frozen")}</SelectItem>
                        <SelectItem value="cancelled">{labelFor("cancelled")}</SelectItem>
                        <SelectItem value="rejected">{labelFor("rejected")}</SelectItem>
                        <SelectItem value="no_show">{labelFor("no_show")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="icon" variant="ghost" onClick={() => setSelected(b)} data-testid={`view-${b.code}`}><Eye className="w-4 h-4" /></Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                          {selected && <BookingDetails booking={selected} onPaid={() => updateStatus(selected.id, "completed")} />}
                        </DialogContent>
                      </Dialog>
                      {openEdit && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-[#1E3A5F] hover:bg-[#1E3A5F]/10"
                          onClick={() => openEdit(b)}
                          data-testid={`edit-booking-${b.code}`}
                          title="Modifica prenotazione (sincronizza tutti i menu)"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                      {duplicateBooking && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-[#AC5D45] hover:bg-[#AC5D45]/10"
                          onClick={() => duplicateBooking(b)}
                          data-testid={`duplicate-booking-${b.code}`}
                          title="Duplica prenotazione (crea copia con date +7 giorni)"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      )}
                      {b.status === "pending" && (
                        <>
                          <Button size="icon" variant="ghost" className="text-emerald-600 hover:bg-emerald-50" onClick={() => updateStatus(b.id, "confirmed")} data-testid={`confirm-${b.code}`}><Check className="w-4 h-4" /></Button>
                          <Button size="icon" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => updateStatus(b.id, "rejected")} data-testid={`reject-${b.code}`}><X className="w-4 h-4" /></Button>
                        </>
                      )}
                      {b.status === "confirmed" && b.receipt && (
                        <Button size="icon" variant="ghost" className="text-violet-600 hover:bg-violet-50" onClick={() => updateStatus(b.id, "completed")} data-testid={`mark-paid-${b.code}`} title="Marca come pagata">
                          <BadgeCheck className="w-4 h-4" />
                        </Button>
                      )}
                      {deleteBooking && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-rose-700 hover:bg-rose-50"
                          onClick={() => deleteBooking(b)}
                          data-testid={`delete-booking-${b.code}`}
                          title="Elimina prenotazione (rimuove da tutti i menu)"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  </>
  );
};

const BookingDetails = ({ booking, onPaid }) => {
  const [receiptUrl, setReceiptUrl] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);

  useEffect(() => {
    if (!booking?.receipt) return;
    let revoke = null;
    setLoadingReceipt(true);
    api
      .get(`/admin/bookings/${booking.id}/receipt`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: booking.receipt.mime || "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        revoke = url;
        setReceiptUrl(url);
      })
      .catch(() => toast.error("Impossibile caricare la ricevuta"))
      .finally(() => setLoadingReceipt(false));
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [booking?.id, booking?.receipt]);

  const isImage = booking.receipt?.mime?.startsWith("image/");
  const isPdf = booking.receipt?.mime === "application/pdf";

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-2xl text-[#1E3A5F]">{booking.code}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 text-sm">
        <Row k="Camera" v={booking.guide_room_number != null ? `#${booking.guide_room_number} · ${booking.room_name}` : booking.room_name} />
        <Row k="Check-in / out" v={`${formatDate(booking.check_in)} → ${formatDate(booking.check_out)} (${booking.nights} notti)`} />
        <Row k="Ospiti" v={booking.guests} />
        <Row k="Tariffa" v={booking.rate_type === "non_refundable" ? "Non rimborsabile (-10%)" : "Standard (acconto 30%)"} />
        <Row k="Pagamento" v={booking.payment_method || "—"} />
        <Row k="Totale" v={`€${Number(booking.total || 0).toFixed(2)}`} />
        <Row k="Acconto" v={`€${Number(booking.deposit_amount || 0).toFixed(2)}`} />
        {/* Saldo da incassare — calcolato live (totale − acconto). Mostrato
            sempre quando > 0 così l'host vede subito quanto deve riscuotere. */}
        {(() => {
          const total = Number(booking.total || 0);
          const dep = Number(booking.deposit_amount || 0);
          const saldo = Math.max(0, total - dep);
          if (saldo <= 0.01) return null;
          return (
            <div className="flex justify-between font-bold bg-amber-50 border border-amber-300 rounded px-2 py-1.5 -mx-1 my-1" data-testid="detail-saldo">
              <span className="text-amber-900">💰 Saldo da incassare</span>
              <span className="text-amber-900">€{saldo.toFixed(2)}</span>
            </div>
          );
        })()}
        {/* Tassa di soggiorno — pagata / non pagata, sincronizzata con la Guide App */}
        <div className="flex justify-between items-center gap-2 -mx-1 px-2 py-1.5 rounded border" data-testid="detail-tassa-row" style={{
          background: booking.tassa_paid ? "#ECFDF5" : "#FFFBEB",
          borderColor: booking.tassa_paid ? "#A7F3D0" : "#FDE68A",
        }}>
          <span className="text-[#1E3A5F] font-medium inline-flex items-center gap-1.5">🏛️ Tassa di soggiorno</span>
          {booking.tassa_paid ? (
            <span className="inline-flex items-center gap-1.5 text-emerald-800 font-bold text-sm whitespace-nowrap" data-testid="detail-tassa-paid">
              ✅ Pagata
              {booking.tassa_amount ? <span className="font-semibold">· €{Number(booking.tassa_amount).toFixed(2)}</span> : null}
              {booking.tassa_method ? <span className="text-[11px] font-normal text-emerald-700">via {booking.tassa_method}</span> : null}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-amber-800 font-bold text-sm whitespace-nowrap" data-testid="detail-tassa-unpaid">
              ⏳ Non pagata
            </span>
          )}
        </div>
        <hr className="my-2" />
        <Row k="Nome" v={`${booking.guest?.first_name || ""} ${booking.guest?.last_name || ""}`.trim() || "—"} />
        <Row k="Email" v={booking.guest?.email || "—"} />
        <Row k="Telefono" v={booking.guest?.phone || "—"} />
        <Row k="Nazione" v={[booking.guest?.country, booking.guest?.city].filter(Boolean).join(" / ") || "—"} />
        <Row k="Data nascita" v={booking.guest?.date_of_birth || "—"} />
        <Row k="Nazionalità" v={booking.guest?.nationality || "—"} />
        {booking.notes && <Row k="Note" v={booking.notes} />}

        {/* Ospite effettivo — quando il prenotante ha indicato un'altra persona
            come destinatario del soggiorno (regalo o familiare). */}
        {(booking.guest_for?.first_name || booking.guest_for?.last_name || booking.guest_for?.email || booking.guest_for?.phone) && (
          <div className="mt-4 p-4 bg-gradient-to-br from-[#FFF5EE] to-[#F5F1EA] border-l-4 border-[#AC5D45] rounded-lg" data-testid="admin-guest-for-section">
            <div className="flex items-start gap-3">
              <span className="text-2xl shrink-0">🎁</span>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#AC5D45]">Ospite effettivo</div>
                <div className="font-serif text-lg text-[#1E3A5F] mt-0.5" data-testid="admin-guest-for-name">
                  {[booking.guest_for.first_name, booking.guest_for.last_name].filter(Boolean).join(" ") || "—"}
                </div>
                <div className="text-xs text-[#635F59] space-y-0.5 mt-1">
                  {booking.guest_for.email && (
                    <div>📧 <a href={`mailto:${booking.guest_for.email}`} className="text-[#AC5D45] hover:underline" data-testid="admin-guest-for-email">{booking.guest_for.email}</a></div>
                  )}
                  {booking.guest_for.phone && (
                    <div>📞 <a href={`tel:${booking.guest_for.phone}`} className="text-[#AC5D45] hover:underline" data-testid="admin-guest-for-phone">{booking.guest_for.phone}</a>
                      {" · "}
                      <a href={`https://wa.me/${booking.guest_for.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-[#25D366] hover:underline">WhatsApp</a>
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-[#635F59] italic mt-1">
                  Prenotazione effettuata da {[booking.guest?.first_name, booking.guest?.last_name].filter(Boolean).join(" ") || "ospite"} per conto di questa persona.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Extras prenotazione — parcheggio, animali, letti, culla, shuttle */}
        {(() => {
          const ex = booking.extras || {};
          const PARKING_LABEL = {
            none: "—",
            free_street: "Gratuito in strada",
            paid_onsite: "A pagamento in loco",
            paid_advance: "Prenotato in anticipo (€5/notte)",
            paid_private: "Privato",
          };
          const rows = [];
          if (ex.parking_choice && ex.parking_choice !== "none") {
            rows.push(["🅿️ Parcheggio", PARKING_LABEL[ex.parking_choice] || ex.parking_choice]);
          }
          if (ex.pets) rows.push(["🐾 Animali", "Sì (+€10 sanificazione)"]);
          if (ex.extra_bed > 0) rows.push(["🛏️ Letto extra", `${ex.extra_bed} × €15/notte`]);
          if (ex.crib) rows.push(["👶 Culla", "Sì (gratuita)"]);
          if (ex.breakfast_celiac) rows.push(["🌾 Colazione gluten-free", "Sì"]);
          if (ex.shuttle_request || (ex.shuttle_requests && ex.shuttle_requests.length)) {
            const count = (ex.shuttle_requests || []).length || 1;
            rows.push(["✈️ Navetta", `${count} richiesta/e`]);
          }
          if (booking.children?.length) {
            rows.push(["👨‍👩‍👧 Bambini", booking.children.map((c) => `${c.age}a`).join(", ")]);
          }
          if (!rows.length) return null;
          return (
            <div className="mt-4 p-4 bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg" data-testid="admin-extras-section">
              <div className="font-semibold text-[#1E3A5F] text-sm mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#AC5D45]" /> Extras
              </div>
              <div className="space-y-1.5 text-xs">
                {rows.map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-3">
                    <span className="text-[#635F59]">{label}</span>
                    <span className="text-[#1E3A5F] text-right font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Service Requests — "Avvisa l'Host" submissions from /servizi page */}
        {(() => {
          const reqs = booking.service_requests || [];
          if (!reqs.length) return null;
          const STATUS_TONES = {
            pending: "bg-amber-100 text-amber-800",
            confirmed: "bg-emerald-100 text-emerald-800",
            done: "bg-blue-100 text-blue-800",
            cancelled: "bg-rose-100 text-rose-700",
          };
          return (
            <div className="mt-4 p-4 bg-[#FFF5EE] border border-[#AC5D45]/30 rounded-lg" data-testid="admin-service-requests-section">
              <div className="font-semibold text-[#1E3A5F] text-sm mb-3 flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#AC5D45]" /> Servizi richiesti dall'ospite ({reqs.length})
              </div>
              <ul className="space-y-2.5">
                {reqs.map((r) => (
                  <li key={r.id} className="bg-white border border-[#E8E2D9] rounded-md p-3 text-xs" data-testid={`svc-req-${r.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="font-semibold text-[#1E3A5F] text-sm flex items-center gap-1.5">
                        {r.service_label}
                        {r.source === "guide" && (
                          <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 tracking-wider" title="Richiesta inviata dalla Guida Digitale">📱 Guida</span>
                        )}
                      </div>
                      <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${STATUS_TONES[r.status] || STATUS_TONES.pending}`}>
                        {r.status || "pending"}
                      </span>
                    </div>
                    <div className="text-[#635F59] space-y-0.5">
                      {(r.preferred_date || r.preferred_time) && (
                        <div>📅 <span className="text-[#1E3A5F]">{r.preferred_date || "—"} {r.preferred_time || ""}</span></div>
                      )}
                      {r.phone && (<div>📞 {r.phone}</div>)}
                      {r.notes && (<div className="italic mt-1">"{r.notes}"</div>)}
                      <div className="text-[10px] text-[#8A8680] mt-1">richiesta del {formatDateTime(r.created_at)}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })()}

        {/* Receipt section */}
        {booking.receipt && (
          <div className="mt-4 p-4 bg-[#FDFBF7] border border-[#E8E2D9] rounded-lg" data-testid="admin-receipt-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-[#1E3A5F]">Ricevuta caricata</span>
              </div>
              <span className="text-xs text-[#635F59]">{formatDateTime(booking.receipt.uploaded_at)}</span>
            </div>
            <div className="text-xs text-[#635F59] mb-3">
              <strong>{booking.receipt.filename}</strong> · {(booking.receipt.size / 1024).toFixed(0)} KB · {booking.receipt.mime}
            </div>
            {loadingReceipt && <div className="text-xs text-[#635F59]">Caricamento ricevuta...</div>}
            {receiptUrl && isImage && (
              <a href={receiptUrl} target="_blank" rel="noreferrer" data-testid="admin-receipt-image-link">
                <img src={receiptUrl} alt="Ricevuta" className="max-h-72 w-full object-contain rounded border border-[#E8E2D9] cursor-zoom-in bg-white" />
              </a>
            )}
            {receiptUrl && isPdf && (
              <iframe src={receiptUrl} title="Ricevuta PDF" className="w-full h-72 border border-[#E8E2D9] rounded bg-white" />
            )}
            {receiptUrl && !isImage && !isPdf && (
              <div className="flex items-center gap-2 text-sm text-[#635F59]">
                <FileText className="w-4 h-4" /> File non visualizzabile in anteprima
              </div>
            )}
            {receiptUrl && (
              <a
                href={receiptUrl}
                download={booking.receipt.filename}
                data-testid="admin-receipt-download"
                className="inline-flex items-center gap-2 mt-3 text-sm text-[#AC5D45] hover:underline"
              >
                <Download className="w-4 h-4" /> Scarica ricevuta
              </a>
            )}
            {booking.status === "confirmed" && onPaid && (
              <Button onClick={onPaid} data-testid="mark-paid-dialog-btn" className="w-full mt-4 bg-violet-600 hover:bg-violet-700 text-white">
                <BadgeCheck className="w-4 h-4 mr-2" /> Marca come pagata (completed)
              </Button>
            )}
          </div>
        )}

        {/* Gift Ticket — available for any non-cancelled booking */}
        {booking.status !== "cancelled" && booking.status !== "rejected" && (
          <div className="mt-4 p-4 bg-gradient-to-br from-[#FFF5EE] to-[#F5F1EA] border border-[#AC5D45]/30 rounded-lg" data-testid="admin-gift-section">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="text-2xl shrink-0">🎁</span>
                <div className="min-w-0">
                  <div className="font-semibold text-[#1E3A5F] text-sm">Ticket Regalo</div>
                  <div className="text-xs text-[#635F59] truncate">
                    {booking.gift_ticket?.token ? `Già creato per ${booking.gift_ticket.recipient_name || "destinatario"}` : "Crea un ticket condivisibile senza prezzo"}
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => setGiftOpen(true)}
                data-testid="admin-open-gift-btn"
                className="bg-[#AC5D45] hover:bg-[#8A4936] text-white shrink-0"
              >
                {booking.gift_ticket?.token ? "Gestisci" : "Crea"}
              </Button>
            </div>
            {/* Quick link + send buttons when a ticket exists */}
            {booking.gift_ticket?.token && (() => {
              const giftUrl = `${window.location.origin}/gift/${booking.gift_ticket.token}`;
              const resumeUrl = `${window.location.origin}/booking/resume/${booking.code}`;
              const handleCopy = async () => {
                try { await navigator.clipboard.writeText(giftUrl); toast.success("Link copiato"); } catch { toast.error("Copia manuale"); }
              };
              const handleSendCustomer = async () => {
                const to = booking.guest?.email;
                if (!to) { toast.error("Manca l'email del cliente"); return; }
                try {
                  await api.post(`/bookings/${booking.code}/gift-ticket/send-email`, {
                    to_email: to,
                    from_name: "B&B Emmanuel",
                  });
                  toast.success(`Email inviata a ${to}`);
                } catch (err) {
                  toast.error(errMsg(err, "Errore invio"));
                }
              };
              return (
                <div className="mt-3 pt-3 border-t border-[#AC5D45]/20 space-y-2">
                  <div className="flex items-center gap-2 bg-white border border-[#E4D5C0] rounded-md px-2 py-1.5">
                    <input readOnly value={giftUrl} className="flex-1 bg-transparent text-[11px] font-mono outline-none truncate" data-testid="admin-gift-url" />
                    <button type="button" onClick={handleCopy} className="text-[10px] font-bold text-[#AC5D45] hover:underline" data-testid="admin-gift-copy">COPIA</button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <a href={giftUrl} target="_blank" rel="noreferrer" data-testid="admin-gift-open">
                      <Button size="sm" variant="outline" className="w-full text-xs border-[#AC5D45] text-[#AC5D45]">Anteprima</Button>
                    </a>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleSendCustomer}
                      disabled={!booking.guest?.email}
                      data-testid="admin-gift-send-customer"
                      className="text-xs border-[#1E3A5F] text-[#1E3A5F]"
                      title={booking.guest?.email ? `Invia a ${booking.guest.email}` : "Cliente senza email"}
                    >
                      📧 Cliente
                    </Button>
                    <a href={`https://wa.me/?text=${encodeURIComponent(`Ciao! Ecco il tuo voucher B&B Emmanuel: ${giftUrl}`)}`} target="_blank" rel="noreferrer" data-testid="admin-gift-wa-share">
                      <Button size="sm" variant="outline" className="w-full text-xs border-[#25D366] text-[#25D366]">💬 WhatsApp</Button>
                    </a>
                  </div>
                  <div className="text-[10px] text-[#635F59] italic pt-1">
                    Link "Le mie prenotazioni" del cliente: <a href={resumeUrl} target="_blank" rel="noreferrer" className="text-[#AC5D45] underline break-all">{resumeUrl}</a>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      <CreateGiftTicketDialog
        open={giftOpen}
        onOpenChange={setGiftOpen}
        bookingCode={booking.code}
        existingGift={booking.gift_ticket}
        defaultSender={[booking.guest?.first_name, booking.guest?.last_name].filter(Boolean).join(" ")}
      />
    </>
  );
};

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4">
    <span className="text-[#635F59]">{k}</span>
    <span className="text-[#2D2926] font-medium text-right">{v}</span>
  </div>
);


const KnowledgeBaseTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/knowledge");
      setItems(res.data);
    } catch {
      toast.error("Errore caricamento Q&A");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reset = () => { setEditingId(null); setQuestion(""); setAnswer(""); };

  const save = async () => {
    if (!question.trim() || !answer.trim()) {
      toast.error("Compila domanda e risposta");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/admin/knowledge/${editingId}`, { question, answer });
        toast.success("Q&A aggiornata");
      } else {
        await api.post("/admin/knowledge", { question, answer });
        toast.success("Q&A aggiunta");
      }
      reset();
      load();
    } catch {
      toast.error("Errore salvataggio");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa Q&A?")) return;
    try {
      await api.delete(`/admin/knowledge/${id}`);
      toast.success("Eliminata");
      load();
    } catch {
      toast.error("Errore eliminazione");
    }
  };

  const syncFromGuide = async () => {
    toast.info("Sincronizzazione dalla guida digitale in corso...");
    try {
      const res = await api.post("/admin/knowledge/sync-from-guide");
      if (res.data.ok) {
        toast.success(`✓ ${res.data.synced} Q&A sincronizzate dalla guida`);
        load();
      } else {
        // Distinguish between "guida non raggiungibile" (gentle info) and real errors
        if (res.data.error && /endpoint|reachable|configured/i.test(res.data.error)) {
          toast.message("La guida digitale non condivide Q&A in questo momento. Puoi gestirle manualmente qui.", { duration: 6000 });
        } else {
          toast.error(res.data.error || "Sincronizzazione non riuscita");
        }
      }
    } catch (e) {
      toast.error(errMsg(e, "Errore sincronizzazione"));
    }
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setQuestion(it.question);
    setAnswer(it.answer);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-6" data-testid="kb-tab">
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-6">
        <h3 className="font-display text-xl text-[#1E3A5F] mb-2">{editingId ? "Modifica Q&A" : "Aggiungi nuova Q&A"}</h3>
        <p className="text-xs text-[#635F59] mb-4">Le voci che aggiungi qui vengono iniettate automaticamente nel concierge digitale del sito (Claude Sonnet). Funziona in tutte le 4 lingue.</p>
        <div className="grid gap-3">
          <div>
            <label className="text-xs text-[#635F59] mb-1 block">Domanda (o pattern es. &quot;Si può fumare?&quot;)</label>
            <Textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={2} data-testid="kb-question" placeholder="Es. Si può cucinare nelle camere?" />
          </div>
          <div>
            <label className="text-xs text-[#635F59] mb-1 block">Risposta</label>
            <Textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={4} data-testid="kb-answer" placeholder="Es. Le camere standard non hanno cucina. L'Appartamento Indipendente sì..." />
          </div>
          <div className="flex gap-2">
            <Button onClick={save} data-testid="kb-save" className="bg-[#AC5D45] hover:bg-[#8A4936] text-white">{editingId ? "Aggiorna" : "Aggiungi al concierge"}</Button>
            {editingId && <Button variant="outline" onClick={reset} data-testid="kb-cancel">Annulla</Button>}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#E8E2D9] rounded-xl">
        <div className="p-4 border-b border-[#E8E2D9] flex items-center justify-between" data-testid="kb-count">
          <span className="text-sm text-[#635F59]">{loading ? "Caricamento..." : `${items.length} Q&A nel concierge`}</span>
          <Button onClick={syncFromGuide} variant="outline" size="sm" data-testid="kb-sync-btn">🔄 Sincronizza dalla guida digitale</Button>
        </div>
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-[#635F59]">Nessuna Q&A. Aggiungine una sopra.</div>
        )}
        {items.map((it) => (
          <div key={it.id} data-testid={`kb-item-${it.id}`} className="p-4 border-b border-[#E8E2D9] last:border-b-0 flex items-start gap-3 hover:bg-[#FDFBF7]">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#1E3A5F] mb-1 flex items-center gap-2">
                <span>D: {it.question}</span>
                {it.source === "guide" && <span className="text-[10px] uppercase bg-[#1E3A5F] text-white px-1.5 py-0.5 rounded">Guida</span>}
              </div>
              <div className="text-sm text-[#2D2926] whitespace-pre-wrap">R: {it.answer}</div>
            </div>
            <div className="flex flex-col gap-1">
              <Button size="icon" variant="ghost" onClick={() => startEdit(it)} data-testid={`kb-edit-${it.id}`} title="Modifica"><Edit2 className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => remove(it.id)} data-testid={`kb-delete-${it.id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


// ============================================================
//  GROUP TOTALS MISMATCH BANNER (iter182c)
//  Rileva prenotazioni di gruppo con `group_total` disallineato
//  dai `total` dei singoli membri. Compare in cima alla lista
//  prenotazioni con un CTA per riconciliare in un tap.
// ============================================================
function GroupTotalsMismatchBanner({ count, onDone }) {
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState(null);
  const [expanded, setExpanded] = useState(false);

  if (!count || count <= 0) return null;

  const preview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/bookings/groups/reconcile-totals?dry_run=true");
      setDetails(r.data);
      setExpanded(true);
    } catch { toast.error("Errore anteprima"); }
    finally { setBusy(false); }
  };

  const reconcile = async () => {
    if (!window.confirm("Riconciliare i totali di gruppo? L'operazione è sicura e ricalcola i totali sommando i prezzi delle singole camere.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/bookings/groups/reconcile-totals?dry_run=false");
      toast.success(`Riconciliati ${r.data?.groups_fixed || 0} gruppi`);
      setDetails(null);
      setExpanded(false);
      onDone && onDone();
    } catch { toast.error("Errore riconciliazione"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-xl border-2 border-amber-400 bg-amber-50 p-4" data-testid="group-mismatch-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-amber-900">
              ⚠️ Totali di gruppo da riconciliare
            </h4>
            <p className="text-[12px] mt-0.5 text-amber-800">
              <b>{count}</b> {count === 1 ? "gruppo ha" : "gruppi hanno"} un totale disallineato rispetto alla somma delle singole camere.
              Questo può capitare se hai modificato prezzo/notti di una camera senza salvare l'intera prenotazione.
              Un tap sistema tutto.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={preview} disabled={busy} data-testid="mismatch-preview" className="border-amber-400 text-amber-900 hover:bg-amber-100">
            Vedi cosa cambia
          </Button>
          <Button size="sm" onClick={reconcile} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mismatch-reconcile">
            ✓ Riconcilia adesso
          </Button>
        </div>
      </div>

      {expanded && details?.details?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-amber-300 max-h-60 overflow-y-auto space-y-1">
          {details.details.map((d) => (
            <div key={d.group_code} className="text-[12px] text-amber-900 flex items-center justify-between gap-2 py-1">
              <span className="font-mono">{d.group_code} <span className="text-amber-700">({d.members} camere)</span></span>
              <span>
                <span className="line-through text-amber-600">€{d.old_group_total.toFixed(2)}</span>
                {" → "}
                <b>€{d.new_group_total.toFixed(2)}</b>
                <span className={`ml-1 ${d.diff > 0 ? "text-emerald-700" : "text-rose-700"}`}>({d.diff > 0 ? "+" : ""}€{d.diff.toFixed(2)})</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ============================================================
//  DEPOSIT OVERPAID BANNER (iter182d)
//  Prenotazioni con acconto > totale (tipicamente hai ridotto il
//  prezzo dopo aver incassato). Non c'è auto-fix: rimborsi tu.
// ============================================================
function DepositOverpaidBanner({ count, onOpenEdit }) {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!count || count <= 0) { setItems([]); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/admin/bookings/deposit-overpaid");
        if (!cancelled) setItems(r.data?.items || []);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [count]);

  if (!count || count <= 0) return null;

  const totOver = items.reduce((s, it) => s + Number(it.overpaid || 0), 0);
  const guestName = (b) =>
    b.guest_name
    || `${b.guest?.first_name || ""} ${b.guest?.last_name || ""}`.trim()
    || b.code || "Ospite";

  return (
    <div className="mb-4 rounded-xl border-2 border-rose-400 bg-rose-50 p-4" data-testid="deposit-overpaid-banner">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-rose-200 text-rose-800 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h4 className="font-semibold text-rose-900">
              💸 Acconti superiori al totale
            </h4>
            <p className="text-[12px] mt-0.5 text-rose-800">
              <b>{count}</b> {count === 1 ? "prenotazione ha" : "prenotazioni hanno"} un acconto versato maggiore del totale attuale
              {totOver > 0 && <> (totale in eccesso: <b>€{totOver.toFixed(2)}</b>)</>}.
              Probabilmente hai ridotto il prezzo dopo l'incasso: valuta un rimborso o riporta il prezzo al valore corretto.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setExpanded((e) => !e)} className="border-rose-400 text-rose-900 hover:bg-rose-100" data-testid="deposit-overpaid-toggle">
          {expanded ? "Nascondi" : "Vedi dettagli"}
        </Button>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-rose-300 max-h-60 overflow-y-auto divide-y divide-rose-200">
          {loading && <div className="text-[12px] text-rose-800 py-2">Carico…</div>}
          {items.map((b) => (
            <div key={b.id} className="py-2 flex items-center justify-between gap-3 flex-wrap text-[12px]">
              <div className="min-w-0 flex-1">
                <div className="text-rose-900">
                  <b>{guestName(b)}</b> <span className="font-mono text-[11px] text-rose-700">· {b.code}</span>
                </div>
                <div className="text-[11px] text-rose-700">
                  {b.check_in} → {b.check_out} · {b.room_name || (b.group_code ? `Gruppo ${b.group_code} (${b.group_size} camere)` : "")}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] text-rose-700">Acconto €{Number(b.deposit_amount).toFixed(2)} · Totale €{Number(b.reference_amount).toFixed(2)}</div>
                <div className="font-semibold text-rose-800">In eccesso: €{Number(b.overpaid).toFixed(2)}</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-rose-400 text-rose-900 hover:bg-rose-100 h-7 text-[11px]"
                onClick={() => onOpenEdit && onOpenEdit(b)}
                data-testid={`deposit-overpaid-edit-${b.code}`}
              >
                Apri
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

